import { Component, DestroyRef, inject, input, OnInit, output, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { EntityService, IEntity } from '@app/core/services/http-services/model/entity.service';

import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, switchMap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Constants } from '@app/shared/helpers/constants';
import { _, TranslatePipe } from '@ngx-translate/core';

import { FormFieldComponent } from '@progress/kendo-angular-inputs';
import { LabelComponent } from '@progress/kendo-angular-label';
import { MultiColumnComboBoxComponent, ComboBoxColumnComponent, ColumnCellTemplateDirective } from '@progress/kendo-angular-dropdowns';

_('App.SelectEntity');

@Component({
  selector: 'app-select-entity',
  templateUrl: './select-entity.component.html',
  styleUrls: ['./select-entity.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: SelectEntityComponent,
    },
  ],
  imports: [FormFieldComponent, LabelComponent, MultiColumnComboBoxComponent, ComboBoxColumnComponent, ColumnCellTemplateDirective, TranslatePipe],
})
export class SelectEntityComponent implements ControlValueAccessor, OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly filterSubject = new Subject<string>();
  selectedEntityIndex: number = null;
  loading: boolean = false;

  /* Dynamic width class we can set from calling parent component */
  label = input<string>('App.SelectEntity');
  showLable = input<boolean>(false);
  entityTypeCode = input<'RESOURCE' | 'ACTIVITY'>('RESOURCE');
  getMaterialStandardProperties = input<boolean>(false);
  disabled = signal<boolean>(false);
  selectedEntity = output<IEntity>();

  private readonly entityTake: number = 20;
  private onChanged: Function = () => {};
  private onTouched: Function = () => {};
  public entities: IEntity[];
  private sub: Subscription;
  private readonly entityService = inject(EntityService);

  constructor() {}

  ngOnInit(): void {
    this.filterSubject
      .pipe(
        debounceTime(Constants.DEFAULT_DEBOUNCE_TIME),
        distinctUntilChanged(),
        filter(term => term.length >= 3),
        switchMap(term => {
          this.loading = true;
          return this.entityService.searchEntities(this.entityTypeCode(), term, this.entityTake, this.getMaterialStandardProperties());
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(d => {
        this.entities = d;
        this.loading = false;
      });
  }

  private getEntityByIndex(entityIndex: number) {
    this.loading = true;
    this.sub = this.entityService.get(entityIndex, true).subscribe(d => {
      if (d) {
        this.entities = [d];
        this.onSelectionChange(d);
      }
      this.loading = false;
    });
  }

  onSelectionChange(entity: IEntity) {
    this.selectedEntityIndex = entity != null ? entity.index : null;
    this.selectedEntity.emit(entity);
    this.onChanged(this.selectedEntityIndex);
  }

  writeValue(value: number): void {
    this.selectedEntityIndex = value;
    if (value > 0) {
      this.getEntityByIndex(this.selectedEntityIndex);
    }
  }

  registerOnChange(onChange: any): void {
    this.onChanged = onChange;
  }

  registerOnTouched(onTouched: any): void {
    this.onTouched = onTouched;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  public handleFilterChange(searchTerm: string): void {
    if (searchTerm.length === 0) {
      this.entities = [];
      return;
    }
    this.filterSubject.next(searchTerm);
  }

  ngOnDestroy(): void {
    if (this.sub) {
      this.sub.unsubscribe();
    }
  }
}
